// ── 미팅지도 공유 상수 + 순수 유틸 함수 ─────────────────────────────────────
// MeetingMap.jsx에서 분리.

import { todayKST, addDaysUtc, dayOfWeekUTC } from '../../utils/date';

// ── 상수 ─────────────────────────────────────────────────────────────────────
export const KST_OFFSET_MS        = 9 * 60 * 60 * 1000;
export const DEFAULT_CENTER       = [37.5665, 126.978];
export const DEFAULT_ZOOM         = 11;
export const MOBILE_BREAKPOINT    = 768;
export const PULL_REFRESH_TRIGGER_PX = 70;
export const PULL_REFRESH_MAX_PX     = 120;
export const PULL_REFRESH_DAMPING    = 0.5;
// Leaflet 마커 div 클래스명 (CSS 인젝션에도 동일하게 사용)
export const MEETING_MARKER_ICON_CLASS = 'meeting-marker-icon';

// 유효 한국 좌표 범위 (해외/이상 좌표 마커 제외용)
export const MAP_KOREA_LAT_MIN = 33.0;
export const MAP_KOREA_LAT_MAX = 39.3;
export const MAP_KOREA_LON_MIN = 124.0;
export const MAP_KOREA_LON_MAX = 132.0;

// 전화번호 후보 정규식 (extractPhoneNumbers에서 사용)
export const PHONE_CANDIDATE_REGEX =
  /(?:^|[^\d])((?:\+82[\s\-()]*\d{1,4}[\s\-()]*\d{3,4}[\s\-()]*\d{4})|(?:0\d{1,3}[\s\-()]*\d{3,4}[\s\-()]*\d{4})|(?:1\d{3}[\s\-()]*\d{4}))(?=[^\d]|$)/g;

// 항상 고정 표시되는 특수 항목 — 동적 피드백 그리드에서 제외
export const FIXED_FEEDBACK_KEYS = ['계약', '미팅인계', '미뤄짐'];

// UI 텍스트 상수
export const TXT = {
  title: '미팅지도',
  map: '지도',
  list: '목록',
  loading: '불러오는 중...',
  empty: '해당 날짜 미팅이 없습니다.',
  noCoord: '좌표 없음',
  noAddress: '주소 없음',
  phone: '전화번호',
  mobilePhone: '휴대폰',
  resultInput: '결과 입력',
  resultEdit: '결과 수정',
  copyAddress: '주소 복사',
  close: '닫기',
  copiedAddress: '주소를 복사했습니다.',
  content: '내용',
  save: '저장',
  saving: '저장 중...',
  feedback: '피드백',
  contracted: '계약완료',
  contractDate: '계약일자',
  ticketCount: '계약 수량',
  resultTitle: '미팅 결과 입력',
  markerGuide: '마커를 누르면 상세 정보가 열립니다.',
  pullHint: '아래로 당겨 새로고침',
  pullReady: '놓으면 새로고침',
  pullLoading: '새로고침 중...',
};

// 미팅 상태별 색상 메타 (badge/marker에서 사용)
export const STATUS_META = {
  contracted: { label: '계약완료', color: '#16a34a', bg: '#dcfce7' },
  visited:    { label: '방문완료', color: '#2563eb', bg: '#dbeafe' },
  pending:    { label: '방문예정', color: '#ca8a04', bg: '#fef9c3' },
};

// ── 유틸리티 함수 ─────────────────────────────────────────────────────────────

// 미팅지도 상품 배지 — 상품명만 표시
// contract_items 배열이 있으면 그걸 사용, 없으면 ticket_type 문자열 fallback
export function ticketLabel(contractItems, ticketType) {
  const arr = Array.isArray(contractItems) ? contractItems : [];
  const normal = arr.filter((i) => !i.isService);
  if (normal.length > 0) return normal.map((i) => i.ticketTypeName || '?').join(', ');
  // fallback: ticket_type 문자열에서 상품명만 추출 (유효기간·장수 제거)
  const raw = String(ticketType || '').trim();
  if (!raw) return '';
  // "여행권(저)(12개월)×100, 동반할인권(12개월)×500" → "여행권(저), 동반할인권"
  // (숫자개월) 유효기간과 ×숫자 장수만 제거 — (저)/(고) 같은 상품명 괄호는 보존
  return raw.split(',').map((s) => s.trim().replace(/\(\d+개월\)/g, '').replace(/×\d+/g, '').trim()).filter(Boolean).join(', ');
}

// 현재 KST 날짜를 'YYYY-MM-DD' 형식으로 반환
export function getKstTodayYmd() {
  // todayKST: utils/date.js 사용 (new Date 직접 사용 금지)
  return todayKST();
}

// 특정 ymd에 days를 더한 날짜를 'YYYY-MM-DD' 형식으로 반환
export function addDaysYmd(ymd, days) {
  // addDaysUtc: utils/date.js 사용 (new Date 직접 사용 금지)
  return addDaysUtc(ymd, days);
}

// 'YYYY-MM-DD' → 'M/D' 형식 탭 레이블
export function toTabLabel(ymd) {
  const match = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return ymd;
  return `${Number(match[2])}/${Number(match[3])}`;
}

// 'YYYY-MM-DD' → '월요일' 형식 요일 레이블
export function toWeekdayLabel(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '-';
  const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
  // dayOfWeekUTC: utils/date.js 사용 (new Date 직접 사용 금지)
  return `${weekdays[dayOfWeekUTC(ymd)]}요일`;
}

// 문자열을 숫자로 파싱 (유한수 아니면 null)
export function parseCoord(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

// 유효한 한국 좌표 범위 내 여부 확인
export function isMapDisplayCoord(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= MAP_KOREA_LAT_MIN && lat <= MAP_KOREA_LAT_MAX
    && lng >= MAP_KOREA_LON_MIN && lng <= MAP_KOREA_LON_MAX;
}

// 마커 글자·색은 마커 표시 설정을 따른다 → markerConfig.js (2026-10-24).
//   끝 글자 = 콜 상품 카테고리(서버 call-meeting.js MARKER_CATEGORY_SQL), 색 = 카테고리 색.
//   예전 '콜 건 사람이 외부직원이면 영·주황, 그 외 항·파랑'(getOwnerMarkerFlag)은 없앴다 — 사용자: "외부직원은 필요없는거야".

// HTML 특수문자 이스케이프 (마커 innerHTML 삽입 시 XSS 방지)
export function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// 전화번호 정규화: 숫자와 + 이외 제거
export function normalizePhone(raw) {
  return String(raw || '').trim().replace(/[^\d+]/g, '');
}

// tel: 링크 생성
export function getDialLink(raw) {
  const source = String(raw || '').trim();
  if (!source) return '';
  return `tel:${normalizePhone(source) || source}`;
}

// 한국 전화번호 형식 유효성 검사
export function isValidDialNumber(dial) {
  const text = String(dial || '').replace(/\s+/g, '');
  if (!text) return false;
  const digits = text.replace(/^\+/, '');
  if (/^0\d{8,11}$/.test(digits)) return true;
  if (/^1\d{7}$/.test(digits))    return true;
  if (/^82\d{8,12}$/.test(digits)) return true;
  return false;
}

// 문자열에서 전화번호 추출 → [{ display, dial }] 배열 반환
export function extractPhoneNumbers(raw) {
  const source = String(raw || '').trim();
  if (!source) return [];
  const result = [];
  const seen = new Set();
  const regex = new RegExp(PHONE_CANDIDATE_REGEX.source, 'g');
  let match = regex.exec(source);
  while (match) {
    const display = String(match[1] || '').trim();
    const dial = normalizePhone(display);
    if (dial && isValidDialNumber(dial) && !seen.has(dial)) {
      seen.add(dial);
      result.push({ display, dial });
    }
    match = regex.exec(source);
  }
  return result;
}
